/**
 * @description This file has methods to trigger analytic events for audience fit recommendations feature
 */

import { ANALYTICS } from '../../constants/common';
import { trackFormInput } from './analyticEvents';

/**
 * @method trackAudienceFitByTargetAudience
 * @description tracks the selected target audience and audience fit recommendations count
 * @param {string} selectedAudience - The selected target audience name
 * @param {number} recommendationsCount - The audience fit recommendations count
 * @example trackAudienceFitByTargetAudience('HCP', 5)
 */
export const trackAudienceFitByTargetAudience = (
  selectedAudience: string,
  recommendationsCount: number
) => {
  trackFormInput(
    ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
    ANALYTICS.STEP,
    selectedAudience,
    recommendationsCount.toString(),
    '1'
  );
};

/**
 * @method trackAudienceFitBySegment
 * @description tracks the selected segment and audience fit recommendations count
 * @param {string} selectedSegment - The selected segment name
 * @param {number} recommendationsCount - The audience fit recommendations count
 * @example trackAudienceFitBySegment('Cardiology', 4)
 */
export const trackAudienceFitBySegment = (
  selectedSegment: string,
  recommendationsCount: number
) => {
  trackFormInput(
    ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
    ANALYTICS.STEP,
    selectedSegment,
    recommendationsCount.toString(),
    '2'
  );
};

/**
 * @method trackAudienceFitScore
 * @description tracks the audience fit recommendation score
 * @param {number} score - The audience fit recommendation score
 * @example trackAudienceFitScore(85)
 */
export const trackAudienceFitScore = (score: number) => {
  trackFormInput(
    ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
    ANALYTICS.STEP,
    'audienceFitScore',
    score.toString(),
    '3'
  );
};

/**
 * @method trackAudienceFitBrand
 * @description tracks the selected brand for audience fit recommendations
 * @param {string} brandName - The selected brand name
 * @example trackAudienceFitBrand('BrandX')
 */
export const trackAudienceFitBrand = (brandName: string) => {
  trackFormInput(
    ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
    ANALYTICS.STEP,
    'brand',
    brandName,
    '4'
  );
};

/**
 * @method trackAudienceFitCountry
 * @description tracks the selected country for audience fit recommendations
 * @param {string} countryName - The selected country name
 * @example trackAudienceFitCountry('United States')
 */
export const trackAudienceFitCountry = (countryName: string) => {
  trackFormInput(
    ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
    ANALYTICS.STEP,
    'country',
    countryName,
    '5'
  );
};

/**
 * @method trackAudienceFitIndication
 * @description tracks the selected indication for audience fit recommendations
 * @param {string} indicationName - The selected indication name
 * @example trackAudienceFitIndication('Diabetes')
 */
export const trackAudienceFitIndication = (indicationName: string) => {
  trackFormInput(
    ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
    ANALYTICS.STEP,
    'indication',
    indicationName,
    '6'
  );
};

/**
 * @method trackAudienceFitUserNtid
 * @description tracks the user encoded ntid for audience fit recommendations
 * @param {string} userEncodedNtid - The encoded user ntid
 * @example trackAudienceFitUserNtid('encoded-ntid-123')
 */
export const trackAudienceFitUserNtid = (userEncodedNtid: string) => {
  if (userEncodedNtid !== '') {
    trackFormInput(
      ANALYTICS.AUDIENCE_FIT_RECOMMENDATIONS_FORM_NAME,
      ANALYTICS.STEP,
      ANALYTICS.USER_ENCODED_NTID_QUESTION_NAME,
      userEncodedNtid,
      '7'
    );
  }
};
